import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box, Text } from 'grommet';
import { useAppTheme } from '@Components/Context';
import {
  Seo, RadioButtonSlider, AppButton, AppRadioButton, SimpleModal, InfoNotice, Spinning,
} from '@Components/Control';
import { withProductAuth } from '@Components/Layout';
import { paths } from '@Components/configs';
import { AuthNavWrapper } from '@Components/Navigation';
import {
  initiateFetchBulkAssetRequest, clearBulkAssetRequest, initiateCreateBulkAssetRequest,
} from '@Actions';
import { AssetUploadRequestsTablePartial } from '@Components/Partial/Product/ContentId';
import { StyledStatusBadge } from '@Components/Partial/Product/ListSearches/StyledListSearches.jsx';
import {
  ProductFormSectionWrapper, ProductFormFieldContainer, ProductFormDropdownSelect,
} from '@Components/Partial/Product/NewSearch';
import { MAX_ASSETS_PER_UPLOAD, ASSET_CSV_HEADERS } from '@Components/Partial/Product/ContentId/constants';
import BulkAssetUploader from './BulkAssetUploader';

const PAGE_MODE_STORAGE_KEY = 'assetUploadPageMode';

const StyledOl = styled.ol`
  padding-left: 1.5em;
  margin: 0;
  line-height: 1.5;

  ol li::marker, ul li::marker {
    font-family: 'Geist', 'Roboto', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
    font-size: ${({ size }) => size ?? '1rem'};
    color: ${({ color }) => color ?? 'var(--page-primary-text)'};
  }
`;

const OwnershipModalContent = ({ textColor }) => (
  <Box gap="1rem">
    <Box gap="0.5rem">
      <Text weight={600} color={textColor}>Overview</Text>
      <Text color={textColor}>
        Specify where you own (or don&apos;t own) the asset.
        {' '}
        <b>By default, ownership is worldwide</b>
        {' '}
        unless you specify otherwise.
      </Text>
    </Box>
    <Box gap="0.5rem">
      <Text weight={600} color={textColor}>Format Rules</Text>
      <Text color={textColor}>
        Use
        {' '}
        <b>pipe (|)</b>
        {' '}
        to separate multiple ownership rules.
        {' '}
        <br />
        Use
        {' '}
        <b>spaces</b>
        {' '}
        to separate multiple countries within the same rule.
        <br />
        Use
        {' '}
        <b>WW</b>
        {' '}
        for worldwide.
        {' '}
        <br />
        Use
        {' '}
        <b>-</b>
        {' '}
        prefix to exclude countries.
        {' '}
        <br />
        Use
        {' '}
        <b>percentage</b>
        {' '}
        (e.g., 0% or 100%) to specify ownership ratio (default is 100%).
      </Text>
    </Box>
    <Box gap="0.5rem">
      <Text weight={600} color={textColor}>Best Practices</Text>
      <Text color={textColor}>
        For multiple countries in same rule: Use spaces (CA GB US).
        <br />
        For different rules: Use pipes (CA GB|US).
        <br />
        For exclusions: Use - prefix (-US CA).
        <br />
        For worldwide: Use WW or leave empty.
      </Text>
    </Box>
    <Box gap="0.5rem">
      <Text weight={600} color={textColor}>Examples</Text>
      <Box>
        <Text color={textColor} weight={500}>(empty): Worldwide ownership</Text>
        <Text color={textColor} weight={500}>WW: Worldwide ownership</Text>
        <Text color={textColor} weight={500}>CA GB: Canada and Great Britain only</Text>
        <Text color={textColor} weight={500}>-US CA: Everywhere except US and CA</Text>
        <Text color={textColor} weight={500}>WW|-US CA: Everywhere except US and CA</Text>
        <Text color={textColor} weight={500}>0%WW: Remove ownership (0% worldwide)</Text>
        <Text color={textColor} weight={500}>JP CA US: Japan, Canada, and US only</Text>
      </Box>
    </Box>
  </Box>
);

OwnershipModalContent.propTypes = {
  textColor: PropTypes.string.isRequired,
};

const ReferenceExclusionsModalContent = ({ textColor }) => (
  <Box gap="1rem">
    <Box gap="0.5rem">
      <Text weight={600} color={textColor}>Overview</Text>
      <Text color={textColor}>
        Reference Exclusions allow users to specify time intervals in a video
        that YouTube should exclude when determining Content ID matches.
      </Text>
    </Box>
    <Box gap="0.5rem">
      <Text weight={600} color={textColor}>Format</Text>
      <Text color={textColor}>
        Pipe-delimited list of intervals:
        {' '}
        <b>interval1|interval2|interval3</b>
        {' '}
        <br />
        Each interval:
        {' '}
        <b>START-END</b>
        {' '}
        where times are in one of these formats:
        <br />
        MM:SS-MM:SS (e.g., 05:00-10:00 for videos &lt; 1 hour)
        <br />
        HH:MM:SS-HH:MM:SS (e.g., 01:30:00-01:35:00 for videos ≥ 1 hour)
        <br />
        MMM:SS-MMM:SS (e.g., 120:00-125:00 for 2+ hour videos)
      </Text>
    </Box>
    <Box gap="0.5rem">
      <Text weight={600} color={textColor}>Validation Rules</Text>
      <Text color={textColor}>
        Seconds must be 00-59.
        <br />
        Start time must be &lt; end time.
        <br />
        Minutes can be any number (supports videos &gt; 1 hour).
      </Text>
    </Box>
    <Box gap="0.5rem">
      <Text weight={600} color={textColor}>Valid Examples</Text>
      <Box>
        <Text color={textColor}>00:10-01:00</Text>
        <Text color={textColor}>00:10-01:00|01:30-02:00</Text>
        <Text color={textColor}>01:30:00-01:35:00</Text>
        <Text color={textColor}>120:00-125:00</Text>
        <Text color={textColor}>00:00-00:30</Text>
      </Box>
    </Box>
    <Box gap="0.5rem">
      <Text weight={600} color={textColor}>Invalid Examples</Text>
      <Box>
        <Text color={textColor}>10-60 (Missing colons)</Text>
        <Text color={textColor}>00:10:01:00 (Missing hyphen)</Text>
        <Text color={textColor}>00:10|01:00 (Missing hyphen in second interval)</Text>
        <Text color={textColor}>00:60-01:00 (Invalid seconds &gt;59)</Text>
        <Text color={textColor}>01:30:00-01:25:00 (Start time &gt; end time)</Text>
      </Box>
    </Box>
  </Box>
);

ReferenceExclusionsModalContent.propTypes = {
  textColor: PropTypes.string.isRequired,
};

const AssetUploadPage = ({
  small,
  loading,
  onboardingDataLoading,
  location,
  fetchBulkAssetRequests,
  clearBulkAssetRequests,
  uploadBulkAssets,
  companyId,
  paymentPending,
  featureConfig,
  bulkAssetRequests = null,
  onboardingData = null,
}) => {
  // Determine if user is in 'redflag' upload mode
  const isRedflagUploadCurrent = onboardingData?.youtubeCid?.uploader === 'redflag';
  const stableIsRedflagUploadRef = React.useRef(isRedflagUploadCurrent);
  if (!onboardingDataLoading) {
    stableIsRedflagUploadRef.current = isRedflagUploadCurrent;
  }
  const isRedflagUpload = stableIsRedflagUploadRef.current;
  // Initialize pageMode from localStorage or default to 'bulk', force 'view' if redflag upload mode
  const getInitialPageMode = () => {
    if (isRedflagUpload) return 'view';
    try {
      const stored = localStorage.getItem(PAGE_MODE_STORAGE_KEY);
      if (stored === 'view' || stored === 'bulk') return stored;
    } catch (e) {
      // Intentionally ignored: localStorage may be unavailable
    }
    return 'bulk';
  };
  const [pageMode, setPageModeState] = React.useState(getInitialPageMode());
  const [currentPage, setCurrentPage] = React.useState(0);
  const [showOwnershipModal, setShowOwnershipModal] = React.useState(false);
  const [showReferenceModal, setShowReferenceModal] = React.useState(false);
  const [assetLabel, setAssetLabel] = React.useState('');
  const [selectedOption, setSelectedOption] = React.useState('option1');
  const [lockedOption, setLockedOption] = React.useState(null);

  const { assetConfig } = featureConfig?.feature || {};
  const { hasAllowlistedChannels } = featureConfig || {};
  const assetOptions = Array.isArray(assetConfig)
    ? assetConfig.map((a) => (a.assetLabel))
    : [];
  const { theme: { page, button }, isDarkMode } = useAppTheme();

  React.useEffect(() => {
    if (assetOptions.length === 1) {
      setAssetLabel(assetOptions[0]);
    }
  }, [featureConfig]);

  const setPageMode = (mode) => {
    setPageModeState(mode);
    try {
      localStorage.setItem(PAGE_MODE_STORAGE_KEY, mode);
    } catch (e) {
      // Intentionally ignored: localStorage may be unavailable
    }
  };

  React.useEffect(() => {
    if (pageMode === 'view') {
      const limit = 50;
      const offset = currentPage * limit;
      if (!paymentPending) fetchBulkAssetRequests({ limit, offset });
    }
    return () => {
      clearBulkAssetRequests();
    };
  }, [currentPage, pageMode]);

  React.useEffect(() => {
    if (isRedflagUpload && pageMode !== 'view') setPageModeState('view');
  }, [isRedflagUpload, pageMode]);

  const handlePage = (pageNum) => {
    setCurrentPage(pageNum);
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
  };

  const handleDownloadExampleCSV = () => {
    const csvContent = `${ASSET_CSV_HEADERS.map((h) => h.key).join(',')}\n`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_asset_upload_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const showRedflagNotice = isRedflagUpload && pageMode === 'view';
  const padding = small ? '1.5rem' : { horizontal: '1.5rem', bottom: '1.5rem', top: showRedflagNotice ? '0.5rem' : '1.5rem' };

  const option1Instructions = (
    <Box>
      <StyledOl color={page.primaryText} size="0.9rem">
        <li>
          <Text size="0.9rem" color={page.primaryText}>
            Select asset files from your device by clicking
            {' '}
            <b>Choose Asset Files</b>
            . Up to
            {' '}
            {MAX_ASSETS_PER_UPLOAD}
            {' '}
            files may be selected per upload, and can be added or removed individually.
          </Text>
        </li>
        <li>
          <Text size="0.9rem" color={page.primaryText}>
            Assets will be uploaded with default metadata that covers most standard cases
            (the Asset Title, Description, Notes, and Match Policy can be edited later by clicking
            into an individual asset&apos;s details). The default values are as follows:
          </Text>
          <Box pad={{ left: '0.75rem', vertical: '0.75rem' }}>
            <Text size="0.9rem" color={page.primaryText} weight={600}>
              Asset Title = filename without extension, e.g. video1
              <br />
              Asset Description = none
              <br />
              Notes = none
              <br />
              Asset Type = Web
              <br />
              Ownership = WW (worldwide)
              <br />
              Reference Exclusions = none
              <br />
              Match Policy = Monetize
            </Text>
          </Box>
        </li>
        <li>
          <Text size="0.9rem" color={page.primaryText}>
            Once files are added, click
            {' '}
            <b>Begin Asset Upload</b>
            {' '}
            to start processing.
          </Text>
        </li>
        <li>
          <Text size="0.9rem" color={page.primaryText}>
            After upload, you can track processing status in the &quot;View requests&quot; tab.
          </Text>
        </li>
      </StyledOl>
    </Box>
  );

  const option2Instructions = (
    <Box>
      <StyledOl color={page.primaryText} size="0.9rem">
        <li>
          <Text size="0.9rem" color={page.primaryText}>
            Download the CSV template which is pre-populated with the correct column headers.
          </Text>
        </li>
        <li>
          <Text size="0.9rem" color={page.primaryText}>
            Fill in asset details for each row using a spreadsheet application. Only
            {' '}
            <b>File Name</b>
            {' '}
            is required (Asset Title, Description, Notes, and Match Policy can be
            edited later by clicking into an individual asset&apos;s details).
            Any blank fields will use the following default values:
          </Text>
          <Box pad={{ left: '0.75rem', vertical: '0.75rem' }}>
            <Text size="0.9rem" color={page.primaryText} weight={600}>
              Asset Title = filename without extension, e.g. video1
              <br />
              Asset Description = none
              <br />
              Notes = none
              <br />
              Asset Type = Web
              <br />
              Ownership = WW (worldwide)
              <br />
              Reference Exclusions = none
              <br />
              Match Policy = Monetize
            </Text>
          </Box>
        </li>
        <li>
          <Text size="0.9rem" color={page.primaryText}>
            Select your completed CSV and Asset Files from your device.
            The CSV will be validated on selection, and if there are any
            errors you will be notified to correct them and re-upload the file
            before proceeding.
          </Text>
        </li>
        <li>
          <Text size="0.9rem" color={page.primaryText}>
            Once asset files and your CSV have been added, click
            {' '}
            <b>Begin Asset Upload</b>
            {' '}
            to start processing.
          </Text>
        </li>
        <li>
          <Text size="0.9rem" color={page.primaryText}>
            After upload, you can track processing status in the &quot;View requests&quot; tab.
          </Text>
        </li>
      </StyledOl>
    </Box>
  );

  const renderInfoNotice = () => (
    <InfoNotice header="Asset upload is currently restricted">
      <Box gap="0.5rem">
        <Text color={page.primaryText} size="0.9rem">
          Your channel allowlist is currently empty.
          Please add at least one channel to enable asset uploads.
        </Text>
        <Text color={page.primaryText} size="0.9rem">
          You can manage your allowlist on the&nbsp;
          <a
            href={paths.productCidAllowlist}
            style={{ color: page.altHighlight, textDecoration: 'underline' }}
          >
            Channel Allowlist
          </a>
                  &nbsp;page. If you need assistance, feel free to contact us at&nbsp;
          <a
            href="mailto:contact@redflagai.co"
            style={{ color: page.altHighlight, textDecoration: 'underline' }}
          >
            contact@redflagai.co
          </a>
        </Text>
      </Box>
    </InfoNotice>
  );

  const renderPageContents = () => {
    if (onboardingDataLoading) {
      return (
        <Box flex background={page.background} pad={padding} justify="center" align="center">
          <Spinning color={page.highlight} size="large" />
        </Box>
      );
    }

    if (pageMode === 'view') {
      return (
        <Box flex background={page.background} pad={padding}>
          {showRedflagNotice && (
            <Box pad={{ bottom: '0.85rem' }}>
              <InfoNotice
                header="Redflag is processing your assets"
                text="You have selected Redflag upload. Our team will process your assets within approximately 48 hours. You can track upload progress here."
              />
            </Box>
          )}
          <AssetUploadRequestsTablePartial
            small={small}
            loading={loading}
            requestsData={bulkAssetRequests}
            handlePage={handlePage}
            currentPage={currentPage}
          />
        </Box>
      );
    }

    const selectDetailButtonLabel = (key) => {
      if (key === 'ownership') {
        return 'view Ownership details';
      }

      if (key === 'referenceExclusions') {
        return 'view Reference Exclusion details';
      }

      return '';
    };

    return (
      <Box background={page.background} pad={padding}>
        {!hasAllowlistedChannels && (
          <Box pad={{ bottom: '0.85rem' }}>
              {renderInfoNotice()}
          </Box>
        )}
        <Box
          flex
          direction={small ? 'column' : 'row'}
          justify={small ? 'start' : 'evenly'}
          gap="1.5rem"
          pad={padding}
          width="100%"
        >
          <ProductFormSectionWrapper
            small={small}
            sectionBg={page.altBg}
            sectionTitle="Bulk Asset Upload"
            titleColor={page.primaryText}
            boxStyle={!small ? { flex: 1, minWidth: 0 } : {}}
          >
            <ProductFormFieldContainer
              small={small}
              primaryText={page.primaryText}
              hintText={page.hintText}
              childGap="0.75rem"
            >
              <Box gap="0.75rem" pad={{ top: '0.75rem' }}>
                <Box direction="row" align="center" gap="0.5rem">
                  <Box width="fit-content">
                    <AppRadioButton
                      name="asset-upload-option1"
                      disabled={!!lockedOption}
                      checked={selectedOption === 'option1'}
                      onChange={() => handleOptionSelect('option1')}
                      borderColor={page.inputBorder}
                      color={page.primaryText}
                      highlightColor={page.highlight}
                    />
                  </Box>
                  <Text size="1rem" color={page.primaryText} weight={600}>
                    Option 1: Upload Asset Files with default metadata
                  </Text>
                </Box>
                <Box pad={{ left: '1.5rem' }}>
                  <Text size="0.9rem" color={page.primaryText} weight={500}>
                    This is the simplest option, and should be used in cases
                    where default values are acceptable and no Reference Exclusions, country-level
                    Ownership rules, or custom Asset Type are needed.
                  </Text>
                </Box>
                <Box direction="row" align="center" gap="0.5rem" margin={{ top: '0.75rem' }}>
                  <Box width="fit-content">
                    <AppRadioButton
                      name="asset-upload-option2"
                      disabled={!!lockedOption}
                      checked={selectedOption === 'option2'}
                      onChange={() => handleOptionSelect('option2')}
                      borderColor={page.inputBorder}
                      color={page.primaryText}
                      highlightColor={page.highlight}
                    />
                  </Box>
                  <Text size="1rem" color={page.primaryText} weight={600}>
                    Option 2: Upload Asset Files with metadata from CSV
                  </Text>
                </Box>
                <Box pad={{ left: '1.5rem' }}>
                  <Text size="0.9rem" color={page.primaryText} weight={500}>
                    Use this option to specify custom metadata for each asset,
                    including Ownership rules, Reference Exclusions, Asset Type,
                    and more.
                  </Text>
                </Box>
              </Box>
              <Box pad={{ vertical: '0.75rem' }} gap="0.75rem">
                <Text size="1.1rem" color={page.primaryText} weight={600}>
                  {selectedOption === 'option1' ? 'Option 1' : 'Option 2'}
                  &nbsp;instructions
                </Text>
                {selectedOption === 'option1' ? option1Instructions : option2Instructions}
              </Box>
            </ProductFormFieldContainer>
            <ProductFormFieldContainer
              small={small}
              primaryText={page.primaryText}
              hintText={page.hintText}
            >
              <Box direction="row" gap="0.5rem" align="center" pad={{ bottom: '0.5rem' }}>
                <Text size="1rem" color={page.primaryText} weight={600}>
                  Asset Label:
                </Text>
                {assetOptions.length > 1 ? (
                  <Box width="fit-content" align="center">
                    <ProductFormDropdownSelect
                      selectable
                      autoSelect
                      label={assetLabel || 'Select asset label'}
                      handleFormValues={(val) => setAssetLabel(val)}
                      simpleOptions={assetOptions}
                    />
                  </Box>
                ) : (
                  <StyledStatusBadge background="#EDFDF5">
                    <Text size="1rem" color="#22684B" weight={600}>
                      {assetLabel}
                    </Text>
                  </StyledStatusBadge>
                )}
              </Box>
              <BulkAssetUploader
                csvHeaders={ASSET_CSV_HEADERS}
                uploadBulkAssets={uploadBulkAssets}
                assetLabel={assetLabel}
                companyId={companyId}
                withCsv={selectedOption === 'option2'}
                onS3UploadStart={() => setLockedOption(selectedOption)}
                paymentPending={paymentPending}
                isRedflagUpload={isRedflagUpload}
                hasAllowlistedChannels={hasAllowlistedChannels}
              />
            </ProductFormFieldContainer>
          </ProductFormSectionWrapper>
          <ProductFormSectionWrapper
            small={small}
            sectionBg={page.altBg}
            sectionTitle="CSV Format & Field Guide"
            titleColor={page.primaryText}
            boxStyle={!small ? { flex: 1, minWidth: 0 } : {}}
          >
            <ProductFormFieldContainer
              small={small}
              primaryText={page.primaryText}
              hintText={page.hintText}
            >
              <Box gap="0.75rem" width="100%">
                <Box gap="0.75rem">
                  <Box pad={{ bottom: '0.25rem' }} border={{ side: 'bottom', color: page.tableBorder }}>
                    <Text size="1rem" weight={700} color={page.primaryText}>
                      Required columns
                    </Text>
                  </Box>
                  {ASSET_CSV_HEADERS.filter((h) => h.required).map((h) => (
                    <Box key={h.key} direction="row">
                      <Text size="0.9rem" color={page.primaryText} weight={600}>
                        {h.key}
                        :&nbsp;
                        <Text size="0.875rem" weight={400}>{h.description}</Text>
                      </Text>
                    </Box>
                  ))}
                </Box>
                <Box gap="0.75rem">
                  <Box pad={{ bottom: '0.25rem' }} border={{ side: 'bottom', color: page.tableBorder }}>
                    <Text size="1rem" weight={700} color={page.primaryText}>
                      Optional columns
                    </Text>
                  </Box>
                  {ASSET_CSV_HEADERS.filter((h) => !h.required).map((h) => (
                    <Box direction="column" gap="0.25rem" key={h.key}>
                      <Box direction="row">
                        <Text size="0.9rem" color={page.primaryText} weight={600}>
                          {h.key}
                          :&nbsp;
                          <Text size="0.875rem" weight={400}>{h.description}</Text>
                          {['ownership', 'referenceExclusions'].includes(h.special) && (
                            <Text
                              key={h.special}
                              as="span"
                              style={{
                                cursor: 'pointer',
                                color: page.altHighlight,
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                display: 'inline-block',
                              }}
                              onClick={() => {
                                if (h.special === 'ownership') setShowOwnershipModal(true);
                                if (h.special === 'referenceExclusions') setShowReferenceModal(true);
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              &nbsp;(
                              {selectDetailButtonLabel(h.special)}
                              )
                            </Text>
                          )}
                        </Text>
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Box margin={{ top: '1rem' }}>
                  <AppButton
                    overrideHover
                    level="dynamicLarge"
                    width="14rem"
                    color={button.primaryText}
                    bgColor={button.primaryBg}
                    fontWeight={600}
                    label="Download CSV Template"
                    onClick={handleDownloadExampleCSV}
                  />
                </Box>
              </Box>
            </ProductFormFieldContainer>
          </ProductFormSectionWrapper>
        </Box>
      </Box>
    );
  };

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Upload assets',
        subTitle: 'Content ID',
        bannerChildrenPosition: 'bottom',
        bannerChildren: !isRedflagUpload && (
          <Box direction="row" justify="between">
            <RadioButtonSlider
              radioOptions={[
                { label: 'Bulk upload', value: 'bulk' },
                { label: 'View requests', value: 'view' },
              ]}
              componentBg={page.altBg}
              textColor={page.primaryText}
              activeButtonBg={isDarkMode ? page.highlight : 'white'}
              updateParent={setPageMode}
              value={pageMode}
            />
          </Box>
        ),
      }}
    >
      <Seo />
      {showOwnershipModal && (
        <SimpleModal
          small={small}
          toggleModal={setShowOwnershipModal}
          modalTitle="Ownership Field Details"
          highlightColor={page.highlight}
        >
          <OwnershipModalContent textColor={page.modalText} />
        </SimpleModal>
      )}
      {showReferenceModal && (
        <SimpleModal
          small={small}
          toggleModal={setShowReferenceModal}
          modalTitle="Reference Exclusions Field Details"
          highlightColor={page.highlight}
        >
          <ReferenceExclusionsModalContent textColor={page.modalText} />
        </SimpleModal>
      )}
      {renderPageContents()}
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    bulkAssetRequests: state.contentIdAssets.bulkAssetRequests,
    loading: state.fetchLoader.dataLoading,
    onboardingData: state.payment.customerOnboardingData,
    onboardingDataLoading: state.payment.customerOnboardingDataLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchBulkAssetRequests: initiateFetchBulkAssetRequest,
    clearBulkAssetRequests: clearBulkAssetRequest,
    uploadBulkAssets: initiateCreateBulkAssetRequest,
  }, dispatch);
}

AssetUploadPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  onboardingDataLoading: PropTypes.bool.isRequired,
  fetchBulkAssetRequests: PropTypes.func.isRequired,
  clearBulkAssetRequests: PropTypes.func.isRequired,
  uploadBulkAssets: PropTypes.func.isRequired,
  bulkAssetRequests: PropTypes.shape({
    requests: PropTypes.arrayOf(PropTypes.shape({
      requestId: PropTypes.string,
      assetLabel: PropTypes.string,
      status: PropTypes.string,
      createdAt: PropTypes.string,
      startedAt: PropTypes.string,
      completedAt: PropTypes.string,
      summary: PropTypes.object,
    })),
    pagination: PropTypes.shape({
      total: PropTypes.number,
      limit: PropTypes.number,
      offset: PropTypes.number,
      hasMore: PropTypes.bool,
    }),
  }),
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  featureConfig: PropTypes.shape({
    feature: PropTypes.shape({
      assetConfig: PropTypes.arrayOf(PropTypes.any),
    }),
    contentIdAssetUploadEnabled: PropTypes.bool,
    hasAllowlistedChannels: PropTypes.bool,
  }).isRequired,
  paymentPending: PropTypes.bool.isRequired,
  onboardingData: PropTypes.shape({
    onboardingId: PropTypes.string.isRequired,
    companyId: PropTypes.number.isRequired,
    paymentPending: PropTypes.bool.isRequired,
    paymentLink: PropTypes.string,
    youtubeCid: PropTypes.shape({
      uploader: PropTypes.string,
      plan: PropTypes.string,
    }),
  }),
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductAuth(AssetUploadPage));
